import { useEffect, useRef, useState } from 'react';

import { useChat } from '../../hooks/useChat';
import type { Chat } from '../../types';
import { getChatCapabilities } from '../../utils/conversation-capabilities';

interface ChatActionsMenuProps {
  chat: Chat;
}

const ChatActionsMenu = ({ chat }: ChatActionsMenuProps) => {
  const { updateChatById, deleteChat } = useChat();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const capabilities = getChatCapabilities(chat);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setConfirmDelete(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleToggleBlock = async () => {
    const nextConsent = chat.consent === 'opted_out' ? 'unknown' : 'opted_out';
    await updateChatById(chat.id, { consent: nextConsent });
    setOpen(false);
  };

  const handleToggleFavorite = async () => {
    await updateChatById(chat.id, { favorite: !chat.favorite });
    setOpen(false);
  };

  const handleToggleAutomode = async () => {
    const nextAutomode = chat.automode === 'on' ? 'off' : 'on';
    await updateChatById(chat.id, { automode: nextAutomode });
    setOpen(false);
  };

  const handleDelete = async () => {
    await deleteChat(chat.id);
    setConfirmDelete(false);
    setOpen(false);
  };

  return (
    <div className="chat-actions-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="chat-actions-trigger"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            setOpen((prev) => !prev);
          }
        }}
        aria-label="Chat acties"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        ⋮
      </button>
      {open && (
        <menu className="chat-actions-menu" role="menu" onClick={(event) => event.stopPropagation()}>
          {capabilities.canChangeConsent && (
            <li>
              <button type="button" role="menuitem" onClick={handleToggleBlock}>
                {chat.consent === 'opted_out' ? 'Deblokkeren' : 'Blokkeren'}
              </button>
            </li>
          )}
          {capabilities.canFavorite && (
            <li>
              <button type="button" role="menuitem" onClick={handleToggleFavorite}>
                {chat.favorite ? 'Niet meer favoriet' : 'Favoriet'}
              </button>
            </li>
          )}
          {capabilities.canAutomode && (
            <li>
              <button type="button" role="menuitem" onClick={handleToggleAutomode}>
                {chat.automode === 'on' ? 'Automode uitschakelen' : 'Automode inschakelen'}
              </button>
            </li>
          )}
          {(capabilities.canChangeConsent || capabilities.canFavorite || capabilities.canAutomode) && <li className="divider" />}
          {confirmDelete ? (
            <li className="confirm-row" role="none">
              <span>Weet je het zeker?</span>
              <div className="confirm-buttons">
                <button type="button" className="danger small" onClick={handleDelete}>
                  Ja
                </button>
                <button
                  type="button"
                  className="secondary small"
                  onClick={() => setConfirmDelete(false)}
                >
                  Nee
                </button>
              </div>
            </li>
          ) : (
            <li>
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={() => setConfirmDelete(true)}
              >
                Verwijderen
              </button>
            </li>
          )}
        </menu>
      )}
    </div>
  );
};

export default ChatActionsMenu;
